"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import { useId, useState } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import type {
	SnackbarCloseReason,
	SnackbarOrigin,
} from "@/components/controls/base-controls";
import {
    Box,
	Button,
	IconButton,
    MuiTelInput,
	Snackbar,
	SnackbarContent,
	Stack,
	TextField,
    useMediaQuery,
    useTheme
} from "@/components/controls/base-controls";
import { CloseIcon } from "@/components/controls/icons";
import { unwrapDal, DalMutationError } from "@/core/dal/unwrap";
import type { ContactFormData } from "@/core/schemas/contact.form.schema";
import { contactFormSchema } from "@/core/schemas/contact.form.schema";
import ContactsService from "@/core/services/contacts.service";
import ServiceRegistry from "@/core/services/service-registry";
import { useLoadingStore } from "@/core/utils/loading.store";

const formatDate = (iso: string, timeZone?: string) => new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: timeZone,
    timeZoneName: 'short',
}).format(new Date(iso));

interface SnackbarState extends SnackbarOrigin {
	open: boolean;
}

const ContactForm = () => {
    const t = useTranslations();

    const { isLoading } = useLoadingStore();

    const theme = useTheme();
    // Returns true on mobile (xs, sm), false on desktop (md, lg, xl)
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [openSnackbar, setOpenSnackbar] = useState<SnackbarState>({
        open: false,
        vertical: "top",
        horizontal: "center",
    });

    const { vertical, horizontal, open } = openSnackbar;

    const email_id = useId();
    const given_name_id = useId();
    const message_id = useId();
    const phone_number_id = useId();
    const family_name_id = useId();

    const {
        control,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm<ContactFormData>({
        mode: "onSubmit",
        resolver: zodResolver(contactFormSchema),
        defaultValues: {
            email: "",
            given_name: "",
            message: "",
            phone_number: "",
            submitted_at: "",
            family_name: ""
        },
    });

    const onSubmit: SubmitHandler<ContactFormData> = async (data) => {
		try {
			const contactsService = ServiceRegistry.get(ContactsService);

            data.submitted_at = formatDate(new Date().toISOString(), Intl.DateTimeFormat().resolvedOptions().timeZone);

			// Unwrap the DalReturn: resolves on success, throws DalMutationError on failure
			unwrapDal(await contactsService.submitContact(data));

			setOpenSnackbar({ vertical: "top", horizontal: "center", open: true });

            // Reset form variables smoothly back to clear default values
			reset();
		} catch (error) {
			console.error("Contact submission failed:", error);
			// Surface the DAL error via its i18n key, falling back to the generic failure message
			alert(
				error instanceof DalMutationError
					? t(error.i18nKey)
					: t("contact.submission.failure"),
			);
		}
	};

    const handleSnackbarClose = (
		_event: React.SyntheticEvent | Event,
		reason?: SnackbarCloseReason,
	) => {
		if (reason === "clickaway") {
			return; // Prevent closing on clickaway
		}
		setOpenSnackbar({ ...openSnackbar, open: false }); // Close only when explicitly triggered or other reasons
	};

    return (
        <Box sx={{ flex: 1, maxWidth: "38em" }}>
            
            <Box 
                component="form"
                noValidate 
                aria-busy={isLoading} aria-describedby="data-processing"
                onSubmit={handleSubmit(onSubmit)}>
            
                <Stack direction={{ xs: "column", md: "row" }} 
                    sx={{
                        alignItems: {xs: "flex-start", md: "center"}, 
                        justifyContent: "flex-start", 
                        rowGap: 2, columnGap: 1, 
                        width: "100%",
                        mb: 2
                    }}
                >
                    <Controller
                        name="given_name"
						control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                sx={{ width: "100%" }}
                                required
                                id={given_name_id}
                                type="text"                                
                                label={t("name.given_name.label")}
                                error={!!errors.given_name}
                                helperText={
                                    errors?.given_name?.message && t(errors.given_name.message as string)
                                }
                                slotProps={{ 
                                    htmlInput: { maxLength: 60 },
                                    inputLabel: { shrink: isMobile ? true : undefined }
                                }}
                                variant="outlined"
                            />
                        )}
                    />

                    <Controller
                        name="family_name"
						control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                sx={{ width: "100%" }}
                                required
                                id={family_name_id}
                                type="text"                                
                                label={t("name.family_name.label")}
                                error={!!errors.family_name}
                                helperText={
                                    errors?.family_name?.message && t(errors.family_name.message as string)
                                }
                                slotProps={{ 
                                    htmlInput: { maxLength: 60 },
                                    inputLabel: { shrink: isMobile ? true : undefined } 
                                }}
                                variant="outlined"
                            />
                        )}
                    />

                </Stack>

                <Stack direction={{ xs: "column", md: "row" }}  
                    sx={{ alignItems: {xs: "center", md: "flex-start"}, justifyContent: "flex-start", 
                    rowGap: 2, columnGap: 1, width: "100%" 
                }}>
                    <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                        <TextField
                            { ...field }
                            fullWidth
                            required
                            id={email_id}
                            type="email"
                            inputMode="email"
                            label={t("email.label")}
                            error={!!errors.email}
                            helperText={
                                errors?.email?.message && t(errors.email.message as string)
                            }
                            slotProps={{ 
                                htmlInput: { maxLength: 255 },
                                inputLabel: { shrink: isMobile ? true : undefined } 
                            }}
                            variant="outlined"
                            sx={{ width: {xs: "100%", md: "60%" } }}            
                        />
                        )}
                    />

                    <Controller
                        name="phone_number"
                        control={control}
                        render={({ field }) => (
                            <MuiTelInput
                                {...field}                                    
                                required
                                id={phone_number_id}
                                label={t("phone_number.label")}
                                error={!!errors.phone_number}
                                helperText={
                                    errors?.phone_number?.message && t(errors.phone_number.message as string)
                                }
                                defaultCountry="US"
                                slotProps={{ 
                                    htmlInput: { maxLength: 20, autoComplete: "new-password" },
                                    inputLabel: { shrink: true }
                                }}
                                sx={{ width: {xs: "100%", md: "40%" } }}
                            />
                        )}
                    />
                </Stack>

                <Controller
                    name="message"
                    control={control}
                    render={({ field }) => (
                    <TextField
                        { ...field }
                        fullWidth
                        id={message_id}
                        type="message"
                        label={t("message.label")}
                        error={!!errors.message}
                        helperText={
                            errors?.message?.message && t(errors.message.message as string)
                        }
                        slotProps={{ 
                            htmlInput: { maxLength: 220 },
                            inputLabel: { shrink: isMobile ? true : undefined } 
                        }}
                        variant="outlined"
                        multiline
                        minRows={4}
                        sx={{ margin: "1em 0 1.5em" }}
                    />
                    )}
                />

                <Stack direction="row"
                    sx={{alignItems: {xs: "flex-end"}, gap: 2, justifyContent: "flex-end", width: "100%"}}>

                    <Button
                        type="button"
                        variant="text"
                        sx={{
                            borderRadius: '5px',
                            padding: "1.2em 2.2em",
                            width: "max-content",
                            minWidth: 0,
                            flexShrink: 1
                        }}
                        onClick={() => reset()}
                        disabled={isLoading}
                    >
                        {t("contact.reset")}
                    </Button>

                    <Button
                        variant="lightBg"
                        sx={{
                            padding: "1.2em 2.2em",
                        }}
                        type="submit"
                        disabled={isLoading}
                    >
                        {t("contact.submission.submit")}
                    </Button>
                </Stack>
            
            </Box> 
            
            <Snackbar
				anchorOrigin={{ vertical, horizontal }}
				open={open}
				onClose={handleSnackbarClose}
				key={vertical + horizontal}
				autoHideDuration={null}
			>
				<SnackbarContent
					message={t("contact.submission.success")}
					sx={{ backgroundColor: "var(--mui-palette-success)" }}
					action={
						<IconButton
							size="small"
							aria-label="close"
							color="inherit"
							onClick={handleSnackbarClose}
						>
							<CloseIcon fontSize="small" />
						</IconButton>
					}
				/>
			</Snackbar>
        
        </Box>
    );
}

export default ContactForm;
